import { useState } from 'react';
import { Box, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Card } from '@liminal/web-ui/primitives/Card';
import { getErrorMessage } from '@/errors/messages';
import { useEventCode, useEventTicketIssuances } from '@/hooks/useEvents';
import { useNow } from '@/hooks/useNow';
import { momentText, operatorText, tallyText } from '../checkIn';
import { copyText } from '../copyText';
import { buildEventIssuancesCsv } from '../issuancesCsv';

const COLUMNS = ['Time', 'Guest', 'Tickets', 'Issued by'] as const;

/**
 * On a phone the "Issued by" column is dropped and who issued shows under the
 * time instead, so the table fits the screen without scrolling sideways.
 */
const WIDE_ONLY = { display: { xs: 'none', sm: 'table-cell' } } as const;
const PHONE_ONLY = { display: { xs: 'block', sm: 'none' } } as const;

/** The times are read against a clock this fresh. */
const CLOCK_TICK_MS = 30_000;

/**
 * Every guest of the event handed tickets, most recent first (LIM-4707): the
 * list the venue's bill is settled from at close, copied as CSV for a
 * spreadsheet. In the server's order, which is the order that matters here,
 * so the columns do not sort.
 */
export function IssuancesPage() {
  const code = useEventCode();
  const now = useNow(CLOCK_TICK_MS);
  const list = useEventTicketIssuances(code);
  const { show, snackbarProps } = useSnackbar();
  const [copyFailed, setCopyFailed] = useState(false);
  const issuances = list.data?.issuances ?? [];

  const copy = async () => {
    if (list.data === undefined) return;
    const copied = await copyText(buildEventIssuancesCsv(list.data.issuances));
    setCopyFailed(!copied);
    if (copied) show(`Copied ${tallyText(list.data.tally.guests, list.data.tally.tickets)} as CSV`);
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {list.data ? tallyText(list.data.tally.guests, list.data.tally.tickets) : 'Every guest handed tickets.'}
        </Typography>
        <Button startIcon="copy" onClick={() => void copy()} disabled={issuances.length === 0}>
          Copy as CSV
        </Button>
      </Stack>

      {copyFailed ? (
        <WarningCard tone="error">
          The browser did not let this page use the clipboard. Allow clipboard access for this site, or open the
          console over HTTPS, then copy again.
        </WarningCard>
      ) : null}

      <Card variant="elevated" padding="none">
        {/* Four columns are wider than a phone: the table scrolls inside its card, the page does not. */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column} sx={column === 'Issued by' ? WIDE_ONLY : undefined}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMNS.length}
              isLoading={list.isPending}
              isError={list.isError}
              isEmpty={issuances.length === 0}
              loadingRows={4}
              emptyMessage="No guest has been handed tickets yet."
              emptyIcon="calendar"
              errorMessage={list.isError ? getErrorMessage(list.error) : undefined}
            >
              {issuances.map((issuance) => (
                <TableRow key={issuance.userId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {momentText(issuance.issueTime, now)}
                    <Typography variant="caption" color="text.secondary" sx={PHONE_ONLY}>
                      {operatorText(issuance.actor)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{issuance.guestDisplayName || 'No profile'}</Typography>
                    {issuance.guestDisplayTag ? (
                      <Typography variant="caption" color="text.secondary">
                        @{issuance.guestDisplayTag}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{issuance.ticketCount}</TableCell>
                  <TableCell sx={WIDE_ONLY}>{issuance.actor}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </Box>
      </Card>

      <Snackbar {...snackbarProps} />
    </Stack>
  );
}
